/**
 * ペルソナの編集。
 *
 * 更新は expectedVersion を添えて送る。他の画面が先に更新していたら
 * サーバーが CONFLICT を返すので、上書きせずに「読み直してからやり直す」導線を出す。
 */

import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useMutation, useQuery } from "urql";
import {
  Button,
  Card,
  DeletePersonaDocument,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  PersonaDocument,
  Tag,
  UpdatePersonaDocument,
  formatJstDateTime,
} from "visumo-data-agent-web-shared";

import { HelpIcon } from "../help/HelpIcon";

import { BrandContextsCard } from "./BrandContextsCard";
import { PersonaForm, type PersonaUserError } from "./PersonaForm";
import { PersonaQuestionsCard } from "./PersonaQuestionsCard";

export function PersonaEditPage() {
  const { personaId } = useParams();
  const navigate = useNavigate();
  const [result, refetch] = useQuery({
    query: PersonaDocument,
    variables: { id: personaId ?? "" },
    pause: personaId === undefined,
  });
  const [state, updatePersona] = useMutation(UpdatePersonaDocument);
  const [deleteState, deletePersona] = useMutation(DeletePersonaDocument);
  const [userErrors, setUserErrors] = useState<PersonaUserError[]>([]);
  const [deleteErrors, setDeleteErrors] = useState<PersonaUserError[]>([]);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const reload = (): void => refetch({ requestPolicy: "network-only" });

  if (result.fetching && result.data === undefined) {
    return <LoadingState />;
  }
  if (result.error !== undefined) {
    return (
      <ErrorState
        description={result.error.message}
        onRetry={() => refetch({ requestPolicy: "network-only" })}
      />
    );
  }
  const persona = result.data?.persona ?? null;
  if (persona === null) {
    return (
      <div className="vd-page">
        <EmptyState title="このペルソナは見つかりませんでした" />
      </div>
    );
  }

  const conflicted = userErrors.some((error) => error.code === "CONFLICT");

  // 参考の初期値（DERIVED）は実測から機械的に起こしたもので、書き換える口が無い。
  // 編集させる代わりに、複製してブランド定義のものを作る導線へ返す。
  if (persona.origin === "DERIVED") {
    return (
      <div className="vd-page">
        <PageHeader
          title={persona.name}
          description={`参考の初期値 ・ 最終更新 ${formatJstDateTime(persona.updatedAt)}`}
          titleExtra={<HelpIcon slug="personas/edit" />}
        />
        <Card
          title={
            <>
              {persona.name}
              <Tag tone="info">参考の初期値</Tag>
            </>
          }
        >
          <p style={{ marginTop: 0 }}>{persona.description}</p>
          <p className="vd-card-note" style={{ marginBottom: 0 }}>
            これは実測から機械的に起こした人物像で、そのままでは書き換えられません。
            一覧から複製すると、ブランドが定義したペルソナとして編集できます。
          </p>
          <div className="vd-row">
            <Button onClick={() => navigate("/personas")}>一覧へ戻る</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="vd-page">
      <PageHeader
        title={persona.name}
        description={`版 ${persona.version} ・ 最終更新 ${formatJstDateTime(persona.updatedAt)}`}
        titleExtra={<HelpIcon slug="personas/edit" />}
      />
      <PersonaForm
        // 読み直したら入力欄も最新の値から始める
        key={`${persona.id}-${persona.version}`}
        mode="edit"
        initial={{
          name: persona.name,
          description: persona.description,
          attributes: [...persona.attributes],
          barriers: [...persona.barriers],
          questions: persona.questions.map((question) => question.question),
        }}
        userErrors={userErrors}
        submitting={state.fetching}
        notice={
          conflicted ? (
            <div className="vd-notice" role="alert">
              <p style={{ marginTop: 0 }}>
                このペルソナは他の画面で更新されています。いまの入力のまま保存すると
                その変更を消してしまうので、最新を読み込んでからやり直してください。
              </p>
              <Button
                onClick={() => {
                  setUserErrors([]);
                  refetch({ requestPolicy: "network-only" });
                }}
              >
                最新を読み込む
              </Button>
            </div>
          ) : undefined
        }
        onCancel={() => navigate("/personas")}
        onSubmit={(values) => {
          void updatePersona({
            input: {
              personaId: persona.id,
              expectedVersion: persona.version,
              name: values.name,
              description: values.description,
              attributes: values.attributes,
              barriers: values.barriers,
            },
          }).then((mutationResult) => {
            if (mutationResult.error !== undefined) {
              setUserErrors([
                {
                  field: null,
                  code: "INTERNAL_ERROR",
                  message: `保存できませんでした: ${mutationResult.error.message}`,
                },
              ]);
              return;
            }
            const payload = mutationResult.data?.updatePersona;
            if (payload === undefined) {
              return;
            }
            if (payload.userErrors.length > 0) {
              setUserErrors([...payload.userErrors]);
              return;
            }
            setUserErrors([]);
            navigate("/personas");
          });
        }}
      />

      <div className="vd-stack" style={{ marginTop: 16 }}>
        <PersonaQuestionsCard persona={persona} onChanged={reload} />
        <BrandContextsCard persona={persona} onChanged={reload} />

        <Card
          title="このペルソナを削除する"
          note="削除すると、このペルソナで数えたカバレッジは画面から消えます。商品や FAQ は消えません。"
        >
          {/*
            断られた理由は**ここで出す**。フォームの入力欄に紐づける口が無い
            ので（削除に「名前」や「説明」は無い）、上のフォーム側へ回すと
            field 付きのエラーが誰にも表示されないまま消える
          */}
          {deleteErrors.length === 0 ? null : (
            <div className="vd-notice vd-notice--error" role="alert">
              {deleteErrors.map((error) => (
                <p key={`${error.code}-${error.message}`} style={{ margin: 0 }}>
                  {error.message}
                </p>
              ))}
              <p style={{ margin: "4px 0 0" }}>
                すでに他の画面で消されているか、内容が変わっています。
                <Button
                  onClick={() => {
                    setDeleteErrors([]);
                    reload();
                  }}
                >
                  最新を読み込む
                </Button>
              </p>
            </div>
          )}
          {confirmingDelete ? (
            <div className="vd-notice" role="alert">
              <p style={{ marginTop: 0 }}>
                「{persona.name}」を削除します。取り消せません。
              </p>
              <div className="vd-row">
                <Button
                  disabled={deleteState.fetching}
                  onClick={() => {
                    void deletePersona(
                      {
                        input: {
                          personaId: persona.id,
                          expectedVersion: persona.version,
                        },
                      },
                      // 削除の応答には Persona が入っていない（返るのは id だけ）
                      // ので、そのままでは一覧のキャッシュが古いまま残り、
                      // 消したはずのペルソナが一覧に出続ける
                      { additionalTypenames: ["Persona"] },
                    ).then((mutationResult) => {
                      if (mutationResult.error !== undefined) {
                        setDeleteErrors([
                          {
                            field: null,
                            code: "INTERNAL_ERROR",
                            message: `削除できませんでした: ${mutationResult.error.message}`,
                          },
                        ]);
                        return;
                      }
                      const payload = mutationResult.data?.deletePersona;
                      if (payload === undefined) {
                        return;
                      }
                      if (payload.userErrors.length > 0) {
                        setDeleteErrors([...payload.userErrors]);
                        setConfirmingDelete(false);
                        return;
                      }
                      setDeleteErrors([]);
                      navigate("/personas");
                    });
                  }}
                >
                  削除する
                </Button>
                <Button
                  disabled={deleteState.fetching}
                  onClick={() => setConfirmingDelete(false)}
                >
                  やめる
                </Button>
              </div>
            </div>
          ) : (
            <Button onClick={() => setConfirmingDelete(true)}>
              このペルソナを削除する
            </Button>
          )}
        </Card>
      </div>
    </div>
  );
}
